<template>
  <div class="mt-2">
    <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center py-5">
      <div class="spinner-border text-primary mb-2" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div class="text-primary">Loading session details...</div>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <i class="fas fa-exclamation-triangle fa-2x text-warning mb-3"></i>
      <h5 class="text-muted mb-3">Failed to load session details</h5>
      <p class="text-muted mb-3">{{ error }}</p>
      <button class="btn btn-outline-primary" @click="$emit('retry')">
        <i class="fas fa-refresh me-2"></i>
        Retry
      </button>
    </div>

    <div v-else-if="!session || !details" class="card">
      <div class="card-body">
        <h5 class="card-title">Session Details</h5>
        <p class="text-muted mb-0">Select a 2.0 session to inspect its details.</p>
      </div>
    </div>

    <div v-else class="card">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-start">
          <div class="d-flex flex-column flex-grow-1">
            <h5 class="card-title mb-2">{{ sessionHeader.title || 'Untitled Session' }}</h5>
            <div class="metadata-inline mb-2">
              <span class="me-2"><strong>ID:</strong> {{ sessionHeader.id }}</span>
              <span class="me-2"><strong>User:</strong> {{ getUserName(sessionHeader.userId) || 'N/A' }}</span>
              <span class="me-2"><strong>Customer:</strong> {{ getCustomerName(sessionHeader.customerId) || 'N/A' }}</span>
              <span class="me-2"><strong>Model:</strong> {{ sessionHeader.model || 'N/A' }}</span>
              <span class="me-2"><strong>Created:</strong> {{ formatTimestamp(sessionHeader.createdAt) }}</span>
              <span class="me-2"><strong>Type:</strong> {{ formatInitiationType(sessionHeader.initiationType) || 'N/A' }}</span>
              <span v-if="contextLabel"><strong>Context:</strong> {{ contextLabel }}</span>
            </div>
            <div class="metadata-inline">
              <span class="me-3"><strong>Exchanges:</strong> {{ getStatValue('exchanges') }}</span>
              <span class="me-3"><strong>Total Tokens:</strong> {{ formatNumber(getStatValue('totalTokens')) }} tokens</span>
              <span class="me-3"><strong>Total Tool Calls:</strong> {{ getStatValue('totalToolCalls') }}</span>
              <span class="me-3"><strong>Avg Response:</strong> {{ getStatValue('avgResponse') }}</span>
              <span class="me-3"><strong>Total Duration:</strong> {{ getStatValue('totalDuration') }}</span>
              <span class="me-3"><strong>Thumbs Up:</strong> {{ getStatValue('thumbsUp') }}</span>
              <span><strong>Thumbs Down:</strong> {{ getStatValue('thumbsDown') }}</span>
            </div>
          </div>
          <div class="d-flex flex-wrap align-items-center justify-content-end gap-2">
            <div v-if="exchanges.length" class="d-flex align-items-center gap-1">
              <span class="text-muted small text-nowrap"><strong>Jump to:</strong></span>
              <select
                v-model="selectedJumpIndex"
                class="form-select form-select-sm"
                style="width: auto; min-width: 250px; max-width: 360px;"
                @change="onJumpSelect"
              >
                <option disabled :value="null">Select a turn...</option>
                <option
                  v-for="(exchange, index) in exchanges"
                  :key="exchange.id"
                  :value="index"
                >
                  {{ getExchangeJumpLabel(exchange, index) }}
                </option>
              </select>
            </div>
            <button class="btn btn-outline-secondary btn-sm text-nowrap" @click="copyAllMessages">
              <i class="fas fa-copy me-1"></i> Copy All Messages
            </button>
            <button class="btn btn-outline-secondary btn-sm text-nowrap" @click="$emit('backToList')">
              <i class="fas fa-arrow-left me-1"></i>
              Back to List
            </button>
          </div>
        </div>
      </div>

      <div class="card-body">
        <div v-if="attachments.length" class="attachments-banner mb-3">
          <strong class="me-2">Attachments:</strong>
          <span
            v-for="attachment in attachments"
            :key="attachment.id"
            class="badge bg-secondary me-1"
            :title="`${attachment.mimeType} · ${formatNumber(attachment.sizeBytes)} bytes · ${attachment.status}`"
          >
            {{ attachment.filename }}
          </span>
        </div>

        <div v-if="!exchanges.length" class="alert alert-info mb-0">
          No turns found for this session.
        </div>

        <div v-else class="conversation-cards-container">
          <div
            v-for="(exchange, index) in exchanges"
            :key="exchange.id"
            class="conversation-turn mb-3"
            :data-exchange-id="exchange.id"
          >
            <div v-if="exchange.user" class="conversation-card user-prompt">
              <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="turn-info">
                    <span class="turn-number">User Prompt #{{ index + 1 }}</span>
                  </div>
                  <div class="d-flex align-items-center">
                    <div class="timestamp me-2">{{ formatTimestamp(exchange.user.createdAt) }}</div>
                    <button
                      class="btn btn-sm btn-outline-secondary me-1"
                      @click="copyText(exchange.user.text, 'User Prompt')"
                      title="Copy User Prompt Text"
                    >
                      <font-awesome-icon :icon="faCopy" />
                    </button>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      @click="copyJson(exchange.user.requestJson, 'User Prompt JSON')"
                      title="Copy User Prompt JSON"
                    >
                      <strong>JSON</strong>
                    </button>
                  </div>
                </div>
              </div>
              <div class="card-content">{{ exchange.user.text || '(empty)' }}</div>
            </div>

            <div v-if="exchange.assistant" class="conversation-card ai-response">
              <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="turn-info">
                    <span class="turn-number">AI Response #{{ index + 1 }}</span>
                    <span
                      v-if="exchange.assistant.status && exchange.assistant.status !== 'ok'"
                      class="badge bg-danger ms-2"
                    >
                      {{ exchange.assistant.status }}
                    </span>
                  </div>
                  <div class="d-flex align-items-center">
                    <div class="timestamp me-2">{{ formatTimestamp(exchange.assistant.createdAt) }}</div>
                    <button
                      class="btn btn-sm btn-outline-secondary me-1"
                      @click="copyText(exchange.assistant.text, 'AI Response')"
                      title="Copy AI Response Text"
                    >
                      <font-awesome-icon :icon="faCopy" />
                    </button>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      @click="copyJson(exchange.assistant, 'AI Response JSON')"
                      title="Copy AI Response JSON"
                    >
                      <strong>JSON</strong>
                    </button>
                  </div>
                </div>
              </div>
              <div class="card-content">
                <div
                  v-if="exchange.assistant.text"
                  class="markdown-content"
                  v-html="parseMarkdown(exchange.assistant.text)"
                ></div>
                <div v-else class="text-muted fst-italic">No assistant text.</div>
                <div v-if="exchange.assistant.errorText" class="alert alert-danger mt-2 mb-0">
                  {{ exchange.assistant.errorText }}
                </div>
              </div>
              <div class="card-footer">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex align-items-center flex-wrap">
                    <div class="metrics me-3">
                      <span class="metric-item me-2">{{ formatNumber(exchange.assistant.totalTokens) }} tokens</span>
                      <span class="metric-item me-2">{{ formatResponseTime(exchange.assistant.latencyMs) }}</span>
                      <span class="metric-item me-2">{{ exchange.assistant.toolCalls?.length || 0 }} tool calls</span>
                      <span
                        v-if="exchange.assistant.inputTokens || exchange.assistant.outputTokens"
                        class="metric-item me-2"
                      >
                        in {{ formatNumber(exchange.assistant.inputTokens) }} / out {{ formatNumber(exchange.assistant.outputTokens) }}
                      </span>
                    </div>
                    <template v-for="(promptId, promptIndex) in (exchange.assistant.systemPromptIds || [])" :key="`${promptId}-${promptIndex}`">
                      <button
                        v-if="promptId !== 0"
                        class="btn btn-sm btn-outline-secondary me-2"
                        @click="showSystemPromptModal(promptId, promptIndex)"
                      >
                        {{ getPromptLabel(promptIndex) }} : {{ promptId }}
                      </button>
                    </template>
                  </div>
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    @click="toggleDetails(exchange)"
                  >
                    <i class="fas fa-chevron-down me-1" v-if="!exchange.showDetails"></i>
                    <i class="fas fa-chevron-up me-1" v-else></i>
                    Response Details
                  </button>
                </div>
              </div>
            </div>

            <div v-if="exchange.showDetails && exchange.assistant" class="response-details">
              <div class="detail-card request-context">
                <div class="card-header">
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="turn-number">Request JSON</span>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      @click="copyJson(exchange.assistant.requestJson, 'Request JSON')"
                    >
                      <font-awesome-icon :icon="faCopy" />
                    </button>
                  </div>
                </div>
                <div class="card-content">
                  <pre class="json-content">{{ stringify(exchange.assistant.requestJson) }}</pre>
                </div>
              </div>

              <div class="detail-card feature-context">
                <div class="card-header">
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="turn-number">Context Ref</span>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      @click="copyJson(exchange.assistant.contextRef || exchange.user?.contextRef, 'Context Ref')"
                    >
                      <font-awesome-icon :icon="faCopy" />
                    </button>
                  </div>
                </div>
                <div class="card-content">
                  <pre v-if="exchange.assistant.contextRef || exchange.user?.contextRef" class="json-content">
{{ stringify(exchange.assistant.contextRef || exchange.user?.contextRef) }}
                  </pre>
                  <div v-else class="no-data-message">No context ref for this exchange</div>
                </div>
              </div>

              <div class="detail-card metadata">
                <div class="card-header">
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="turn-number">Response JSON</span>
                    <div class="d-flex gap-1">
                      <button
                        class="btn btn-sm btn-outline-secondary"
                        @click="copyJson(exchange.assistant.responseJson, 'Response JSON')"
                        title="Copy Response JSON"
                      >
                        <font-awesome-icon :icon="faCopy" />
                      </button>
                      <button
                        class="btn btn-sm btn-outline-secondary"
                        @click="toggleResponseJson(exchange.id)"
                      >
                        <i
                          class="fas me-1"
                          :class="isResponseJsonExpanded(exchange.id) ? 'fa-chevron-up' : 'fa-chevron-down'"
                        ></i>
                        {{ isResponseJsonExpanded(exchange.id) ? 'Hide' : 'Show' }}
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="isResponseJsonExpanded(exchange.id)" class="card-content">
                  <pre v-if="exchange.assistant.responseJson" class="json-content">{{ stringify(exchange.assistant.responseJson) }}</pre>
                  <div v-else class="no-data-message">No response JSON for this turn</div>
                </div>
              </div>

              <div v-if="exchange.user?.manualFilters" class="detail-card request-context">
                <div class="card-header">
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="turn-number">Manual Filters</span>
                    <button
                      class="btn btn-sm btn-outline-secondary"
                      @click="copyJson(exchange.user.manualFilters, 'Manual Filters')"
                    >
                      <font-awesome-icon :icon="faCopy" />
                    </button>
                  </div>
                </div>
                <div class="card-content">
                  <pre class="json-content">{{ stringify(exchange.user.manualFilters) }}</pre>
                </div>
              </div>

              <div v-if="exchange.assistant.toolCalls?.length" class="tool-calls-section">
                <div class="detail-card tool-calls-card">
                  <div class="card-header">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="turn-number">Tool Calls</span>
                      <button
                        class="btn btn-sm btn-outline-secondary"
                        @click="copyJson(exchange.assistant.toolCalls, 'All Tool Calls')"
                      >
                        <font-awesome-icon :icon="faCopy" />
                      </button>
                    </div>
                  </div>

                  <div class="tool-call-tabs">
                    <button
                      v-for="toolCall in exchange.assistant.toolCalls"
                      :key="toolCall.id"
                      type="button"
                      class="tool-call-tab"
                      :class="{ active: selectedToolCall(exchange)?.id === toolCall.id }"
                      @click="selectToolCall(exchange.id, toolCall.id)"
                    >
                      {{ formatToolName(toolCall.toolName) }}
                      <span class="badge ms-1" :class="toolCall.status === 'ok' ? 'bg-success' : 'bg-danger'">
                        {{ toolCall.status }}
                      </span>
                    </button>
                  </div>

                  <div v-if="selectedToolCall(exchange)" class="card-content tool-call-tab-content">
                    <div v-if="selectedToolCall(exchange).narration" class="tool-call-section">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <strong>Narration</strong>
                        <button
                          class="btn btn-sm btn-outline-secondary"
                          @click="copyText(selectedToolCall(exchange).narration, 'Tool Narration')"
                        >
                          <font-awesome-icon :icon="faCopy" />
                        </button>
                      </div>
                      <div
                        class="markdown-content tool-call-narration"
                        v-html="parseMarkdown(selectedToolCall(exchange).narration)"
                      ></div>
                    </div>

                    <div class="tool-call-section">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <strong>Metadata</strong>
                        <button
                          class="btn btn-sm btn-outline-secondary"
                          @click="copyJson(selectedToolCall(exchange), 'Tool Call')"
                        >
                          <font-awesome-icon :icon="faCopy" />
                        </button>
                      </div>
                      <div class="tool-call-metadata">
                        <span><strong>ID:</strong> {{ selectedToolCall(exchange).id }}</span>
                        <span><strong>Status:</strong> {{ selectedToolCall(exchange).status }}</span>
                        <span>
                          <strong>Latency:</strong>
                          {{ formatResponseTime(selectedToolCall(exchange).latencyMs) }}
                        </span>
                        <span v-if="selectedToolCall(exchange).rowCount != null">
                          <strong>Rows:</strong> {{ formatNumber(selectedToolCall(exchange).rowCount) }}
                        </span>
                        <span>
                          <strong>Output size:</strong>
                          {{ formatNumber(selectedToolCall(exchange).outputSizeBytes) }} bytes
                        </span>
                        <span v-if="selectedToolCall(exchange).hasFullOutput">
                          <strong>Full output:</strong>
                          <button
                            class="btn btn-sm btn-outline-primary ms-1"
                            :disabled="isToolResultDownloading(selectedToolCall(exchange).id)"
                            @click="downloadToolResult(selectedToolCall(exchange))"
                          >
                            <span
                              v-if="isToolResultDownloading(selectedToolCall(exchange).id)"
                              class="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <i v-else class="fas fa-download me-1"></i>
                            {{
                              isToolResultDownloading(selectedToolCall(exchange).id)
                                ? 'Downloading...'
                                : 'Download JSON'
                            }}
                          </button>
                        </span>
                        <span v-else><strong>Full output:</strong> Not stored</span>
                        <span><strong>Created:</strong> {{ formatTimestamp(selectedToolCall(exchange).createdAt) }}</span>
                      </div>
                      <div
                        v-if="selectedToolCall(exchange).errorText"
                        class="alert alert-danger py-1 px-2 mt-2 mb-0"
                      >
                        {{ selectedToolCall(exchange).errorText }}
                      </div>
                    </div>

                    <div class="tool-call-section">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <strong>Input</strong>
                        <button
                          class="btn btn-sm btn-outline-secondary"
                          @click="copyJson(selectedToolCall(exchange).inputJson, 'Tool Input')"
                        >
                          <font-awesome-icon :icon="faCopy" />
                        </button>
                      </div>
                      <pre>{{ stringify(selectedToolCall(exchange).inputJson) }}</pre>
                    </div>

                    <div class="tool-call-section mb-0">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <strong>Output Preview</strong>
                        <button
                          class="btn btn-sm btn-outline-secondary"
                          @click="copyJson(selectedToolCall(exchange).outputPreview, 'Tool Output Preview')"
                        >
                          <font-awesome-icon :icon="faCopy" />
                        </button>
                      </div>
                      <pre>{{ stringify(selectedToolCall(exchange).outputPreview) }}</pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="exchange.assistant?.feedback"
              :class="exchange.assistant.feedback === 'up' ? 'conversation-card feedback-positive' : 'conversation-card feedback-negative'"
            >
              <div class="card-header">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="turn-number">Feedback</span>
                  <div class="timestamp">{{ formatTimestamp(exchange.assistant.feedbackAt) }}</div>
                </div>
              </div>
              <div class="card-content">
                <div class="feedback-content">
                  <div class="feedback-category">
                    {{ exchange.assistant.feedback === 'up' ? 'Thumbs Up' : 'Thumbs Down' }}
                  </div>
                </div>
              </div>
            </div>

            <hr v-if="index < exchanges.length - 1" class="interaction-separator" />
          </div>
        </div>
      </div>
    </div>

    <PromptViewModal
      v-if="showPromptModal"
      :show="showPromptModal"
      :loading="promptModalLoading"
      :type="promptModalHeader?.type"
      :title="promptModalHeader?.title"
      :featureType="promptModalHeader?.featureType"
      :version="promptModalHeader?.version"
      :order="promptModalHeader?.order"
      :isActive="promptModalHeader?.isActive"
      :createdAt="promptModalHeader?.createdAt"
      :content="promptModalData?.content"
      :description="promptModalData?.description"
      @close="onClosePromptModal"
    />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import InsightsService from '../../services/InsightsService.js';
import PromptViewModal from '../../components/PromptViewModal.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { parseMarkdown } from '../../utils/markdown.js';
import { startCase } from 'lodash';

export default {
  name: 'SessionDetailsV2',
  components: { PromptViewModal, FontAwesomeIcon },
  props: {
    session: {
      type: Object,
      default: null,
    },
    details: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: null,
    },
  },
  emits: ['backToList', 'retry'],
  data() {
    return {
      model: streetmetrics.model,
      insightsService: new InsightsService(),
      showPromptModal: false,
      promptModalLoading: false,
      promptModalData: null,
      promptModalHeader: null,
      expandedExchangeIds: {},
      expandedResponseJsonIds: {},
      selectedToolCallIds: {},
      downloadingToolCallIds: {},
      selectedJumpIndex: null,
      faCopy,
    };
  },
  computed: {
    sessionHeader() {
      return this.details?.session || this.session || {};
    },
    attachments() {
      return this.details?.attachments || [];
    },
    contextLabel() {
      const ref = this.sessionHeader.contextRef;
      if (!ref) return '';
      if (ref.label) return ref.label;
      return ref.kind || '';
    },
    exchanges() {
      const turns = this.details?.turns || [];
      const result = [];
      for (let i = 0; i < turns.length; i++) {
        const turn = turns[i];
        if (turn.role === 'user') {
          const next = turns[i + 1];
          const assistant = next?.role === 'assistant' ? next : null;
          if (assistant) i += 1;
          const id = assistant?.id || turn.id;
          result.push({
            id,
            user: turn,
            assistant,
            showDetails: Boolean(this.expandedExchangeIds[id]),
          });
        } else if (turn.role === 'assistant') {
          result.push({
            id: turn.id,
            user: null,
            assistant: turn,
            showDetails: Boolean(this.expandedExchangeIds[turn.id]),
          });
        }
      }
      return result;
    },
  },
  methods: {
    parseMarkdown(text) {
      return parseMarkdown(text);
    },
    stringify(value) {
      return JSON.stringify(value ?? null, null, 2);
    },
    formatTimestamp(timestamp) {
      return timestamp ? dayjs(timestamp).format('MMM D, YYYY h:mm A') : 'N/A';
    },
    formatInitiationType(type) {
      if (!type) return '';
      return type
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    formatToolName(toolName) {
      if (!toolName) return 'Unknown Tool';
      return toolName
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    formatNumber(num) {
      if (num == null) return '0';
      return Number(num).toLocaleString();
    },
    formatResponseTime(ms) {
      if (!ms) return '0ms';
      if (ms < 1000) return `${ms}ms`;
      return `${(ms / 1000).toFixed(1)}s`;
    },
    getUserName(userId) {
      if (!userId || !this.model.users) return null;
      const user = this.model.users.find(u => u.userId === userId);
      return user ? user.displayName : null;
    },
    getCustomerName(customerId) {
      if (!customerId || !this.model.customers) return null;
      const customer = this.model.customers.find(c => c.customerId === customerId);
      return customer ? customer.customerName : null;
    },
    getPromptLabel(index) {
      if (index === 0) return 'Base';
      if (index === 1) return 'Narration';
      if (index === 2) return 'Pricing';
      if (index === 3) return 'Org';
      return `User ${index - 3}`;
    },
    formatPromptType(promptType) {
      if (!promptType) return '';
      return startCase(String(promptType).replace(/_/g, ' '));
    },
    getStatValue(statType) {
      const assistants = this.exchanges.map(e => e.assistant).filter(Boolean);
      switch (statType) {
        case 'exchanges':
          return this.exchanges.length;
        case 'totalTokens':
          return assistants.reduce((sum, turn) => sum + (turn.totalTokens || 0), 0);
        case 'totalToolCalls':
          return assistants.reduce((sum, turn) => sum + (turn.toolCalls?.length || 0), 0);
        case 'avgResponse': {
          const times = assistants.map(t => t.latencyMs).filter(t => t && t > 0);
          if (!times.length) return '0ms';
          const avg = times.reduce((sum, t) => sum + t, 0) / times.length;
          return this.formatResponseTime(avg);
        }
        case 'totalDuration': {
          const turns = this.details?.turns || [];
          if (turns.length < 2) return '0s';
          const first = new Date(turns[0].createdAt);
          const last = new Date(turns[turns.length - 1].createdAt);
          const durationMs = last - first;
          if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
          const minutes = Math.floor(durationMs / 60000);
          const seconds = Math.round((durationMs % 60000) / 1000);
          return `${minutes}m ${seconds}s`;
        }
        case 'thumbsUp':
          return assistants.filter(t => t.feedback === 'up').length;
        case 'thumbsDown':
          return assistants.filter(t => t.feedback === 'down').length;
        default:
          return 0;
      }
    },
    toggleDetails(exchange) {
      this.expandedExchangeIds = {
        ...this.expandedExchangeIds,
        [exchange.id]: !this.expandedExchangeIds[exchange.id],
      };
    },
    isResponseJsonExpanded(exchangeId) {
      return Boolean(this.expandedResponseJsonIds[exchangeId]);
    },
    toggleResponseJson(exchangeId) {
      this.expandedResponseJsonIds = {
        ...this.expandedResponseJsonIds,
        [exchangeId]: !this.expandedResponseJsonIds[exchangeId],
      };
    },
    selectedToolCall(exchange) {
      const toolCalls = exchange.assistant?.toolCalls || [];
      const selectedId = this.selectedToolCallIds[exchange.id];
      return toolCalls.find(toolCall => toolCall.id === selectedId) || toolCalls[0] || null;
    },
    selectToolCall(exchangeId, toolCallId) {
      this.selectedToolCallIds = {
        ...this.selectedToolCallIds,
        [exchangeId]: toolCallId,
      };
    },
    isToolResultDownloading(toolCallId) {
      return Boolean(this.downloadingToolCallIds[toolCallId]);
    },
    async downloadToolResult(toolCall) {
      if (!toolCall?.id || !this.sessionHeader.id || this.isToolResultDownloading(toolCall.id)) {
        return;
      }

      this.downloadingToolCallIds = {
        ...this.downloadingToolCallIds,
        [toolCall.id]: true,
      };
      try {
        const payload = await this.insightsService.getAdminSessionToolResult(
          this.sessionHeader.id,
          toolCall.id,
        );
        const blob = new Blob([JSON.stringify(payload, null, 2)], {
          type: 'application/json',
        });
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const safeToolName = String(toolCall.toolName || 'tool-result').replace(
          /[^a-zA-Z0-9_-]+/g,
          '-',
        );
        link.href = objectUrl;
        link.download = `${safeToolName}-${toolCall.id}.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
        this.toast('Full tool result downloaded!');
      } catch (error) {
        const status = error?.status || error?.response?.status;
        const message =
          status === 410
            ? 'Full tool result has expired or is no longer available'
            : 'Failed to download full tool result';
        this.toast(message, status === 410 ? 'bg-warning' : 'bg-danger');
      } finally {
        this.downloadingToolCallIds = {
          ...this.downloadingToolCallIds,
          [toolCall.id]: false,
        };
      }
    },
    getExchangeJumpLabel(exchange, index) {
      const userPrompt = String(exchange.user?.text || '').trim().replace(/\s+/g, ' ');
      const preview = userPrompt.slice(0, 50);
      const feedbackMark = exchange.assistant?.feedback ? ' 💬' : '';
      const errorMark =
        exchange.assistant?.status && exchange.assistant.status !== 'ok' ? ' ⚠' : '';
      if (preview) {
        const ellipsis = userPrompt.length > 50 ? '…' : '';
        return `Turn ${index + 1}: ${preview}${ellipsis}${feedbackMark}${errorMark}`;
      }
      return `Turn ${index + 1}${feedbackMark}${errorMark}`;
    },
    onJumpSelect() {
      if (this.selectedJumpIndex == null) return;
      this.jumpToExchange(this.selectedJumpIndex);
    },
    jumpToExchange(index) {
      const exchange = this.exchanges[index];
      if (!exchange) return;
      this.$nextTick(() => {
        const el = this.$el.querySelector(`[data-exchange-id="${exchange.id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    toast(message, background = 'bg-success') {
      this.model.toast.message = null;
      this.$nextTick(() => {
        this.model.toast.message = message;
        this.model.toast.background = background;
      });
    },
    async copyText(text, label) {
      try {
        await navigator.clipboard.writeText(text || '');
        this.toast(`${label} copied to clipboard!`);
      } catch (error) {
        this.toast(`Failed to copy ${label}`, 'bg-danger');
      }
    },
    async copyJson(value, label) {
      try {
        await navigator.clipboard.writeText(this.stringify(value));
        this.toast(`${label} copied to clipboard!`);
      } catch (error) {
        this.toast(`Failed to copy ${label}`, 'bg-danger');
      }
    },
    async copyAllMessages() {
      try {
        const header = this.sessionHeader;
        const userName = this.getUserName(header.userId) || 'Unknown User';
        const customerName = this.getCustomerName(header.customerId) || 'Unknown Company';
        let text = `${header.title || 'Untitled Session'}\n${userName} - ${customerName}\n${this.formatTimestamp(header.createdAt)}\n\n`;

        this.exchanges.forEach((exchange, index) => {
          const turnNumber = index + 1;
          text += `${userName} #${turnNumber}: ${exchange.user?.text || 'N/A'}\n`;
          text += `AI Assistant #${turnNumber}: ${exchange.assistant?.text || 'N/A'}\n`;
          if (index < this.exchanges.length - 1) {
            text += '\n-----\n\n';
          }
        });

        await navigator.clipboard.writeText(text);
        this.toast('All messages copied to clipboard!');
      } catch (error) {
        this.toast('Failed to copy all messages', 'bg-danger');
      }
    },
    async showSystemPromptModal(promptId, index) {
      this.showPromptModal = true;
      this.promptModalLoading = true;
      this.promptModalData = null;
      this.promptModalHeader = null;
      const promptLabel = this.getPromptLabel(index);

      try {
        if (index === 0 || index === 1 || index === 2) {
          const fullPrompt = await this.insightsService.getSystemPromptContent(promptId);
          this.promptModalHeader = {
            type: 'system',
            title: fullPrompt.promptName
              ? `${fullPrompt.promptName} System Prompt`
              : `${promptLabel} System Prompt (ID: ${promptId})`,
            featureType: fullPrompt.promptType ? this.formatPromptType(fullPrompt.promptType) : promptLabel.toLowerCase(),
            version: fullPrompt.version || null,
            isActive: fullPrompt.isActive !== undefined ? fullPrompt.isActive : true,
            createdAt: fullPrompt.createdAt ? this.formatTimestamp(fullPrompt.createdAt) : null,
          };
          this.promptModalData = {
            content: fullPrompt.promptContent || 'No content available',
          };
        } else if (index === 3) {
          const orgPrompt = await this.insightsService.getOrganizationPromptById(promptId);
          const customerName = this.getCustomerName(orgPrompt.customerId) || `Customer ${orgPrompt.customerId}`;
          this.promptModalHeader = {
            type: 'organization',
            title: `${promptLabel} Prompt (ID: ${promptId})`,
            featureType: customerName,
            version: orgPrompt.version || null,
            isActive: orgPrompt.isActive !== undefined ? orgPrompt.isActive : true,
            createdAt: orgPrompt.createdAt ? this.formatTimestamp(orgPrompt.createdAt) : null,
          };
          this.promptModalData = {
            content: orgPrompt.promptContent || 'No content available',
          };
        } else {
          const fragment = await this.insightsService.getPromptFragmentById(promptId);
          if (!fragment) throw new Error('Fragment not found');
          const fragmentTypeLabel = fragment.promptType ? this.formatPromptType(fragment.promptType) : 'Fragment';
          const fragmentNameLabel = fragment.promptName ? this.formatPromptType(fragment.promptName) : '';
          this.promptModalHeader = {
            type: 'fragment',
            title: `${promptLabel} Fragment (ID: ${promptId})`,
            featureType: fragmentNameLabel ? `${fragmentTypeLabel} - ${fragmentNameLabel}` : fragmentTypeLabel,
            version: fragment.version || null,
            isActive: fragment.isActive !== undefined ? fragment.isActive : true,
            createdAt: fragment.createdAt ? this.formatTimestamp(fragment.createdAt) : null,
          };
          this.promptModalData = {
            content: fragment.promptContent || 'No content available',
            description: fragment.description || null,
          };
        }
      } catch (error) {
        console.error('Error fetching prompt content:', error);
        this.promptModalHeader = {
          type: index === 3 ? 'organization' : (index >= 4 ? 'fragment' : 'system'),
          title: `${promptLabel} Prompt (ID: ${promptId})`,
          featureType: promptLabel.toLowerCase(),
          version: null,
          isActive: true,
          createdAt: null,
        };
        this.promptModalData = {
          content: 'Error loading prompt content. Please try again.',
        };
      } finally {
        this.promptModalLoading = false;
      }
    },
    onClosePromptModal() {
      this.showPromptModal = false;
      this.promptModalData = null;
      this.promptModalHeader = null;
    },
  },
};
</script>

<style>
@import '../../assets/css/markdown.scss';
</style>

<style scoped>
.card {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.card-title {
  color: #495057;
  font-weight: 600;
}

.metadata-inline {
  font-size: 0.8rem;
  color: #6c757d;
  line-height: 1.2;
}

.conversation-cards-container {
  height: 65vh;
  overflow-y: auto;
  padding-right: 1rem;
}

.conversation-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-right: 1rem;
}

.conversation-card.user-prompt {
  border-left: 4px solid #007bff;
  background-color: #e3f2fd;
}

.conversation-card.ai-response {
  border-left: 4px solid #6f42c1;
  background-color: #f3e5f5;
}

.conversation-card.feedback-positive {
  border-left: 4px solid #28a745;
  background-color: #e8f5e8;
}

.conversation-card.feedback-negative {
  border-left: 4px solid #dc3545;
  background-color: #fde8e8;
}

.conversation-card .card-header {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
  background-color: rgba(255, 255, 255, 0.7);
}

.conversation-card .card-content {
  padding: 1rem;
}

.conversation-card .card-footer {
  padding: 0.75rem 1rem;
  border-top: 1px solid #dee2e6;
  background-color: rgba(255, 255, 255, 0.7);
}

.turn-number {
  font-weight: 600;
  color: #495057;
}

.timestamp {
  font-size: 0.8rem;
  color: #6c757d;
}

.metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.metric-item {
  font-size: 0.8rem;
  color: #6c757d;
  background-color: #e9ecef;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
}

.response-details {
  margin-left: 2rem;
  margin-right: 1rem;
  margin-top: 1rem;
}

.detail-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  overflow: hidden;
}

.detail-card .card-header {
  background: #e9ecef;
  border-bottom: 1px solid #dee2e6;
  padding: 0.5rem 1rem;
}

.detail-card .card-content {
  padding: 1rem;
}

.detail-card.request-context {
  background-color: #e3f2fd;
  border-left: 4px solid #17a2b8;
}

.detail-card.feature-context {
  background-color: #e0f2f1;
  border-left: 4px solid #00838f;
}

.detail-card.metadata {
  background-color: #f8f9fa;
  border-left: 4px solid #6c757d;
}

.detail-card.tool-calls-card {
  background-color: #f3f4f6;
  border-left: 4px solid #7c3aed;
}

.tool-call-tabs {
  display: flex;
  gap: 0.25rem;
  padding: 0.75rem 1rem 0;
  overflow-x: auto;
  border-bottom: 1px solid #dee2e6;
}

.tool-call-tab {
  flex: 0 0 auto;
  padding: 0.5rem 0.75rem;
  color: #495057;
  background: transparent;
  border: 1px solid transparent;
  border-bottom: 0;
  border-radius: 0.375rem 0.375rem 0 0;
}

.tool-call-tab:hover {
  background-color: rgba(124, 58, 237, 0.08);
}

.tool-call-tab.active {
  color: #5b21b6;
  background-color: #fff;
  border-color: #dee2e6;
  margin-bottom: -1px;
}

.tool-call-tab-content {
  background-color: #fff;
}

.tool-call-section {
  margin-bottom: 1rem;
}

.tool-call-section + .tool-call-section {
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

.tool-call-narration {
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-left: 3px solid #7c3aed;
  border-radius: 0.25rem;
}

.tool-call-narration :deep(p:last-child) {
  margin-bottom: 0;
}

.tool-call-metadata {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.5rem 1rem;
  color: #495057;
  font-size: 0.85rem;
}

.tool-call-section pre,
.json-content {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.5rem;
  font-size: 0.8rem;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.no-data-message {
  color: #6c757d;
  font-style: italic;
  text-align: center;
  padding: 1rem;
}

.interaction-separator {
  border: none;
  border-top: 1px dashed #e0e0e0;
  margin: 1.5rem 0;
}

.feedback-category {
  font-weight: 600;
}

.attachments-banner {
  font-size: 0.85rem;
}
</style>
